import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';
import { PropertyOverlay } from '../components/PropertyOverlay';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
  useMap,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GOOGLE_MAPS_API_KEY = 'AIzaSyBNpVf2wxLdPMDj6_VAKG_KM179d7sV2vI';
const PROPERTY_CENTER = { lat: 44.866701, lng: -77.381202 };

const SURFACES = [
  { id: 'dirt', label: 'Dirt' },
  { id: 'gravel', label: 'Gravel' },
  { id: 'water', label: 'Water' },
  { id: 'ice', label: 'Ice' },
  { id: 'overgrown', label: 'Overgrown' },
];

const DIFFICULTIES = [
  { id: 'easy', label: 'Easy', color: '#28A745' },
  { id: 'moderate', label: 'Moderate', color: '#FFC107' },
  { id: 'hard', label: 'Hard', color: '#DC3545' },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: '#28A745', moderate: '#FFC107', hard: '#DC3545',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Trail {
  id: string;
  name: string;
  surface: string;
  difficulty: string;
  path: Array<{ lat: number; lon: number }>;
}

type PanelMode =
  | { type: 'none' }
  | { type: 'drawing' }
  | { type: 'create' }
  | { type: 'edit'; trail: Trail }
  | { type: 'view'; trail: Trail };

// ---------------------------------------------------------------------------
// Trail Polyline Renderer (uses Maps JS API directly)
// ---------------------------------------------------------------------------

function TrailPolylines({ trails, activeId, drawingPath, editId, editPath, onTrailClick }: {
  trails: Trail[];
  activeId: string | null;
  drawingPath: Array<{ lat: number; lng: number }>;
  editId?: string | null;
  editPath?: Array<{ lat: number; lon: number }>;
  onTrailClick: (trail: Trail) => void;
}) {
  const map = useMap();
  const overlaysRef = useRef<google.maps.Polyline[]>([]);

  useEffect(() => {
    if (!map) return;

    // Clear old
    overlaysRef.current.forEach((p) => p.setMap(null));
    overlaysRef.current = [];

    // Draw existing trails
    for (const trail of trails) {
      const pathToUse = (editId && trail.id === editId && editPath) ? editPath : trail.path;
      if (!pathToUse || pathToUse.length < 2) continue;
      const isActive = trail.id === activeId;
      const polyline = new google.maps.Polyline({
        path: pathToUse.map((p) => ({ lat: p.lat, lng: p.lon })),
        strokeColor: DIFFICULTY_COLORS[trail.difficulty] || '#FFC107',
        strokeWeight: isActive ? 5 : 3,
        strokeOpacity: isActive ? 1.0 : 0.7,
        map,
      });
      polyline.addListener('click', () => onTrailClick(trail));
      overlaysRef.current.push(polyline);
    }

    // Draw active drawing path
    if (drawingPath.length >= 2) {
      const drawingLine = new google.maps.Polyline({
        path: drawingPath,
        strokeColor: '#00BFFF',
        strokeWeight: 4,
        strokeOpacity: 1.0,
        icons: [{
          icon: { path: google.maps.SymbolPath.FORWARD_OPEN_ARROW },
          offset: '100%',
        }],
        map,
      });
      overlaysRef.current.push(drawingLine);
    }

    return () => {
      overlaysRef.current.forEach((p) => p.setMap(null));
    };
  }, [map, trails, activeId, drawingPath, editId, editPath, onTrailClick]);

  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TrailsPage() {
  const [trails, setTrails] = useState<Trail[]>([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<PanelMode>({ type: 'none' });

  // Drawing state
  const [drawingPath, setDrawingPath] = useState<Array<{ lat: number; lng: number }>>([]);

  // Form state
  const [formName, setFormName] = useState('');
  const [formSurface, setFormSurface] = useState('dirt');
  const [formDifficulty, setFormDifficulty] = useState('moderate');
  const [editPath, setEditPath] = useState<Array<{ lat: number; lon: number }>>([]);

  const activeTrailId = panel.type === 'view' || panel.type === 'edit' ? (panel as any).trail?.id : null;

  useEffect(() => { loadTrails(); }, []);

  async function loadTrails() {
    setLoading(true);
    const res = await apiFetch<Trail[]>('/api/admin/map/trails');
    if (res.success && res.data) setTrails(res.data);
    setLoading(false);
  }

  // --- Map click: add point when drawing ---
  const handleMapClick = useCallback((e: MapMouseEvent) => {
    if (panel.type !== 'drawing') return;
    if (!e.detail.latLng) return;
    setDrawingPath((prev) => [...prev, { lat: e.detail.latLng!.lat, lng: e.detail.latLng!.lng }]);
  }, [panel.type]);

  // --- Start drawing ---
  function startDrawing() {
    setDrawingPath([]);
    setPanel({ type: 'drawing' });
  }

  // --- Finish drawing -> open form ---
  function finishDrawing() {
    if (drawingPath.length < 2) return;
    setFormName('');
    setFormSurface('dirt');
    setFormDifficulty('moderate');
    setPanel({ type: 'create' });
  }

  // --- Undo last point ---
  function undoPoint() {
    setDrawingPath((prev) => prev.slice(0, -1));
  }

  // --- Cancel drawing ---
  function cancelDrawing() {
    setDrawingPath([]);
    setPanel({ type: 'none' });
  }

  // --- View trail ---
  function viewTrail(trail: Trail) {
    setPanel({ type: 'view', trail });
  }

  // --- Edit trail ---
  function editTrail(trail: Trail) {
    setFormName(trail.name);
    setFormSurface(trail.surface);
    setFormDifficulty(trail.difficulty);
    setEditPath(trail.path ? [...trail.path] : []);
    setPanel({ type: 'edit', trail });
  }

  // --- Save trail ---
  async function saveTrail() {
    const path = panel.type === 'create'
      ? drawingPath.map((p) => ({ lat: p.lat, lon: p.lng }))
      : panel.type === 'edit' ? editPath : [];

    const payload = {
      name: formName,
      surface: formSurface,
      difficulty: formDifficulty,
      path,
    };

    if (panel.type === 'edit') {
      await apiFetch(`/api/admin/map/trails/${(panel as any).trail.id}`, {
        method: 'PATCH', body: JSON.stringify(payload),
      });
    } else {
      await apiFetch('/api/admin/map/trails', {
        method: 'POST', body: JSON.stringify(payload),
      });
    }
    setDrawingPath([]);
    setPanel({ type: 'none' });
    loadTrails();
  }

  // --- Delete trail ---
  async function deleteTrail(trail: Trail) {
    if (!confirm(`Delete trail "${trail.name}"?`)) return;
    await apiFetch(`/api/admin/map/trails/${trail.id}`, { method: 'DELETE' });
    setPanel({ type: 'none' });
    loadTrails();
  }

  // --- Calculate distance ---
  function calcDistance(path: Array<{ lat: number; lon: number }> | Array<{ lat: number; lng: number }>): string {
    let total = 0;
    for (let i = 1; i < path.length; i++) {
      const p1 = path[i - 1];
      const p2 = path[i];
      const lon1 = 'lon' in p1 ? p1.lon : (p1 as any).lng;
      const lon2 = 'lon' in p2 ? p2.lon : (p2 as any).lng;
      const R = 6371e3;
      const dLat = (p2.lat - p1.lat) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
      total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
    return total > 1000 ? `${(total / 1000).toFixed(1)} km` : `${Math.round(total)} m`;
  }

  const isDrawing = panel.type === 'drawing';

  return (
    <div style={s.page}>
      {/* LEFT: Full map */}
      <div style={s.mapCol}>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            defaultCenter={PROPERTY_CENTER}
            defaultZoom={15}
            mapTypeId="satellite"
            gestureHandling="greedy"
            fullscreenControl={false}
            mapId="grimsthorpe-admin-trails"
            onClick={handleMapClick}
            style={{ width: '100%', height: '100%', cursor: isDrawing ? 'crosshair' : undefined }}
          >
            <PropertyOverlay />
            {/* Drawing waypoint markers */}
            {isDrawing && drawingPath.map((pt, i) => (
              <AdvancedMarker key={i} position={pt}>
                <Pin
                  background={i === 0 ? '#28A745' : i === drawingPath.length - 1 ? '#DC3545' : '#00BFFF'}
                  borderColor="#000"
                  glyphColor="#FFF"
                  scale={i === 0 || i === drawingPath.length - 1 ? 0.8 : 0.5}
                />
              </AdvancedMarker>
            ))}

            {/* Trail start-point pins (non-editing) */}
            {!isDrawing && trails.filter((t) => t.path && t.path.length > 0).map((trail) => {
              const isEditingThis = panel.type === 'edit' && (panel as any).trail.id === trail.id;
              if (isEditingThis) return null; // handled below
              return (
                <AdvancedMarker
                  key={`start-${trail.id}`}
                  position={{ lat: trail.path[0].lat, lng: trail.path[0].lon }}
                  title={trail.name}
                  onClick={() => viewTrail(trail)}
                >
                  <Pin background={DIFFICULTY_COLORS[trail.difficulty] || '#FFC107'} borderColor="#000" glyphColor="#FFF" scale={0.8} />
                </AdvancedMarker>
              );
            })}

            {/* Draggable waypoints when editing */}
            {panel.type === 'edit' && editPath.map((pt, i) => (
              <AdvancedMarker
                key={`edit-${i}`}
                position={{ lat: pt.lat, lng: pt.lon }}
                draggable
                onDragEnd={(e) => {
                  if (e.latLng) {
                    setEditPath((prev) => prev.map((p, j) => j === i ? { lat: e.latLng!.lat(), lon: e.latLng!.lng() } : p));
                  }
                }}
              >
                <Pin
                  background={i === 0 ? '#28A745' : i === editPath.length - 1 ? '#DC3545' : '#00BFFF'}
                  borderColor="#00BFFF"
                  glyphColor="#FFF"
                  scale={i === 0 || i === editPath.length - 1 ? 0.8 : 0.5}
                />
              </AdvancedMarker>
            ))}

            {/* Trail info window */}
            {(panel.type === 'view' || panel.type === 'edit') && (() => {
              const trail = (panel as any).trail as Trail;
              if (!trail.path || trail.path.length === 0) return null;
              return (
                <InfoWindow
                  position={{ lat: trail.path[0].lat, lng: trail.path[0].lon }}
                  onCloseClick={() => setPanel({ type: 'none' })}
                  pixelOffset={[0, -40]}
                >
                  <div style={{ color: '#000', minWidth: 160 }}>
                    <strong style={{ fontSize: 14 }}>{trail.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>
                      {trail.difficulty} &middot; {trail.surface} &middot; {trail.path.length} pts &middot; {calcDistance(trail.path)}
                    </p>
                  </div>
                </InfoWindow>
              );
            })()}

            <TrailPolylines
              trails={trails}
              activeId={activeTrailId}
              drawingPath={isDrawing ? drawingPath : []}
              editId={panel.type === 'edit' ? (panel as any).trail.id : null}
              editPath={panel.type === 'edit' ? editPath : undefined}
              onTrailClick={viewTrail}
            />
          </GoogleMap>
        </APIProvider>

        {/* Drawing toolbar */}
        {isDrawing && (
          <div style={s.toolbar}>
            <span style={{ color: '#FFF', fontSize: 13, fontWeight: 500 }}>
              Drawing trail &middot; {drawingPath.length} point{drawingPath.length !== 1 ? 's' : ''}
              {drawingPath.length >= 2 && ` &middot; ${calcDistance(drawingPath)}`}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={s.toolBtn} onClick={undoPoint} disabled={drawingPath.length === 0}>Undo</button>
              <button style={s.toolBtnDanger} onClick={cancelDrawing}>Cancel</button>
              <button style={s.toolBtnPrimary} onClick={finishDrawing} disabled={drawingPath.length < 2}>
                Finish ({drawingPath.length} pts)
              </button>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: Panel */}
      <div style={s.panelCol}>
        <div style={s.panelContent}>
          {/* Default: trail list */}
          {panel.type === 'none' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={s.panelTitle}>Trails</h3>
                <button style={s.btnPrimary} onClick={startDrawing}>+ Draw Trail</button>
              </div>

              {loading ? <div style={{ color: COLORS.textMuted }}>Loading...</div> :
                trails.length === 0 ? (
                  <p style={{ color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' }}>
                    No trails yet. Click "Draw Trail" then click points on the map to create one.
                  </p>
                ) : (
                  trails.map((trail) => (
                    <div key={trail.id} style={s.trailRow} onClick={() => viewTrail(trail)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 24, height: 4, borderRadius: 2, backgroundColor: DIFFICULTY_COLORS[trail.difficulty] || '#FFC107' }} />
                        <div>
                          <div style={{ color: COLORS.textPrimary, fontSize: 13, fontWeight: 500 }}>{trail.name}</div>
                          <div style={{ color: COLORS.textMuted, fontSize: 11 }}>
                            {trail.surface} &middot; {trail.difficulty} &middot; {trail.path?.length || 0} pts &middot; {calcDistance(trail.path || [])}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
            </>
          )}

          {/* Drawing instructions */}
          {panel.type === 'drawing' && (
            <>
              <h3 style={s.panelTitle}>Draw Trail</h3>
              <div style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: '1.6' }}>
                <p style={{ marginBottom: 8 }}><strong style={{ color: COLORS.textPrimary }}>Click</strong> on the map to add waypoints along the trail path.</p>
                <p style={{ marginBottom: 8 }}><strong style={{ color: '#28A745' }}>Green pin</strong> = start point</p>
                <p style={{ marginBottom: 8 }}><strong style={{ color: '#DC3545' }}>Red pin</strong> = current end point</p>
                <p style={{ marginBottom: 8 }}><strong style={{ color: '#00BFFF' }}>Blue line</strong> = trail path</p>
                <p>Click at least 2 points, then hit <strong style={{ color: COLORS.textPrimary }}>Finish</strong> to name and save the trail.</p>
              </div>

              {drawingPath.length > 0 && (
                <div style={{ marginTop: 16, padding: 12, backgroundColor: COLORS.backgroundTertiary, borderRadius: 6 }}>
                  <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                    <strong>{drawingPath.length}</strong> waypoint{drawingPath.length !== 1 ? 's' : ''}
                    {drawingPath.length >= 2 && <> &middot; <strong>{calcDistance(drawingPath)}</strong></>}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Create form (after finishing drawing) */}
          {panel.type === 'create' && (
            <>
              <h3 style={s.panelTitle}>New Trail</h3>
              <div style={{ marginBottom: 12, padding: 10, backgroundColor: COLORS.backgroundTertiary, borderRadius: 6 }}>
                <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                  {drawingPath.length} waypoints &middot; {calcDistance(drawingPath)}
                </span>
              </div>

              <div style={s.field}>
                <label style={s.label}>Name</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} style={s.input} autoFocus placeholder="e.g. East Ridge Loop" />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Surface</label>
                  <select value={formSurface} onChange={(e) => setFormSurface(e.target.value)} style={s.input}>
                    {SURFACES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Difficulty</label>
                  <select value={formDifficulty} onChange={(e) => setFormDifficulty(e.target.value)} style={s.input}>
                    {DIFFICULTIES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.panelActions}>
                <button style={s.btnSecondary} onClick={() => { setDrawingPath([]); setPanel({ type: 'none' }); }}>Cancel</button>
                <button style={s.btnPrimary} onClick={saveTrail} disabled={!formName.trim()}>Create Trail</button>
              </div>
            </>
          )}

          {/* View trail */}
          {panel.type === 'view' && (
            <>
              <h3 style={s.panelTitle}>{panel.trail.name}</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ ...s.badge, backgroundColor: `${DIFFICULTY_COLORS[panel.trail.difficulty]}22`, color: DIFFICULTY_COLORS[panel.trail.difficulty] }}>
                  {panel.trail.difficulty}
                </span>
                <span style={{ ...s.badge, backgroundColor: 'rgba(255,255,255,0.05)', color: COLORS.textMuted }}>
                  {panel.trail.surface}
                </span>
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: 12, marginBottom: 16 }}>
                {panel.trail.path?.length || 0} waypoints &middot; {calcDistance(panel.trail.path || [])}
              </div>
              <div style={s.panelActions}>
                <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deleteTrail(panel.trail)}>Delete</button>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Close</button>
                <button style={s.btnPrimary} onClick={() => editTrail(panel.trail)}>Edit</button>
              </div>
            </>
          )}

          {/* Edit trail */}
          {panel.type === 'edit' && (
            <>
              <h3 style={s.panelTitle}>Edit Trail</h3>
              <div style={{ marginBottom: 12, padding: 10, backgroundColor: COLORS.backgroundTertiary, borderRadius: 6 }}>
                <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                  {panel.trail.path?.length || 0} waypoints &middot; {calcDistance(panel.trail.path || [])}
                </span>
              </div>

              <div style={s.field}>
                <label style={s.label}>Name</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} style={s.input} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Surface</label>
                  <select value={formSurface} onChange={(e) => setFormSurface(e.target.value)} style={s.input}>
                    {SURFACES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Difficulty</label>
                  <select value={formDifficulty} onChange={(e) => setFormDifficulty(e.target.value)} style={s.input}>
                    {DIFFICULTIES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.panelActions}>
                <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deleteTrail(panel.trail)}>Delete</button>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
                <button style={s.btnPrimary} onClick={saveTrail} disabled={!formName.trim()}>Update</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  mapCol: { flex: 1, minWidth: 0, position: 'relative' },
  panelCol: { width: 360, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: 0 },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },

  toolbar: {
    position: 'absolute', bottom: 20, left: 20, right: 20,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 16px', backgroundColor: 'rgba(0,0,0,0.85)', borderRadius: 8,
    zIndex: 10,
  },
  toolBtn: { padding: '6px 14px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFF', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  toolBtnDanger: { padding: '6px 14px', backgroundColor: 'rgba(220,53,69,0.2)', color: '#DC3545', border: '1px solid rgba(220,53,69,0.3)', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  toolBtnPrimary: { padding: '6px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' },

  trailRow: { padding: '10px 8px', marginBottom: 4, borderRadius: 6, border: `1px solid ${COLORS.borderDefault}`, cursor: 'pointer' },

  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },

  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
};
